import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import CheckIcon from "@material-ui/icons/Check";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ImageIcon from "@material-ui/icons/Image";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { toast } from "react-toastify";

import MainContainer from "../../../components/MainContainer";
import MainHeader from "../../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../../components/MainHeaderButtonsWrapper";
import Title from "../../../components/Title";
import AgentIaSelector from "../../../components/AgentIaSelector";

import {
  chatWithAgent,
  saveQna,
  listQna,
  updateQna,
  deleteQna,
  openDocumentFile,
} from "../../../services/agentIa";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    ...theme.scrollbarStyles,
  },
  chatBox: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    minHeight: 120,
    maxHeight: 520,
    overflowY: "auto",
  },
  turn: {
    marginBottom: 16,
  },
  question: {
    fontWeight: 600,
  },
  answerBox: {
    background: "#f5f5f5",
    borderRadius: 8,
    padding: 10,
    marginTop: 6,
    whiteSpace: "pre-wrap",
  },
  verdictRow: {
    display: "flex",
    gap: 8,
    marginTop: 8,
    alignItems: "center",
  },
  askRow: {
    display: "flex",
    gap: 8,
  },
  bankRow: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
  },
  // Fijo a propósito: con muchas respuestas aprobadas, esta tabla no debe
  // crecer sin límite y empujar el chat de arriba fuera de pantalla.
  bankScroll: {
    maxHeight: 280,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  imagePreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  imageThumb: {
    width: 56,
    height: 56,
    objectFit: "cover",
    borderRadius: 6,
    border: "1px solid #ddd",
  },
}));

const STATUS_LABEL = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
};

const AgentIaTraining = () => {
  const classes = useStyles();
  const [agentId, setAgentId] = useState("");
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [turns, setTurns] = useState([]); // {question, answer, sources, editValue, editing, imageUrl}
  // Historial de la sesión de prueba (solo texto) que se manda en cada
  // pregunta para que el agente responda con memoria de la conversación.
  // Vive aparte de `turns` porque un turno puede desaparecer de la vista al
  // aprobarlo/rechazarlo sin que por eso el agente deba "olvidarlo".
  const [history, setHistory] = useState([]);
  const [bank, setBank] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingImageUrl, setPendingImageUrl] = useState(null);
  const bottomRef = useRef(null);
  const imageInputRef = useRef(null);

  const fetchBank = useCallback(async () => {
    if (!agentId) {
      setBank([]);
      return;
    }
    setBankLoading(true);
    try {
      const data = await listQna(agentId);
      setBank(data);
    } catch (err) {
      toastError(err);
    } finally {
      setBankLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    setTurns([]);
    setHistory([]);
    fetchBank();
  }, [agentId, fetchBank]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns]);

  const handleImageSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingImage(file);
    setPendingImageUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setPendingImage(null);
    setPendingImageUrl(null);
  };

  const handleAsk = async () => {
    if ((!question.trim() && !pendingImage) || !agentId) return;
    const q = question.trim();
    const image = pendingImage;
    const imageUrl = pendingImageUrl;
    setQuestion("");
    setPendingImage(null);
    setPendingImageUrl(null);
    setAsking(true);
    try {
      const { answer, sources, equipo_lookups } = await chatWithAgent(
        agentId,
        q,
        image,
        history
      );
      const finalQuestion = q || "(imagen sin texto)";
      setTurns((prev) => [
        ...prev,
        {
          question: finalQuestion,
          imageUrl,
          answer,
          sources,
          equipoLookups: equipo_lookups || [],
          editValue: answer,
          editing: false,
        },
      ]);
      setHistory((prev) => [...prev, { question: finalQuestion, answer }]);
    } catch (err) {
      toastError(err);
      setQuestion(q);
      setPendingImage(image);
      setPendingImageUrl(imageUrl);
    } finally {
      setAsking(false);
    }
  };

  const handleVerdict = async (index, status) => {
    const turn = turns[index];
    const finalAnswer = turn.editing ? turn.editValue : turn.answer;

    try {
      await saveQna(agentId, {
        question: turn.question,
        answer: finalAnswer,
        status,
        source: turn.editing ? "shadow_edit" : "manual",
      });
      // Se queda en el hilo (ya no desaparece): para poder sostener una
      // conversación larga y evaluarla de corrido hay que ver todo el
      // intercambio, no solo el último turno sin resolver. Se marca como
      // resuelto y pierde los botones de acción; el banco de abajo sigue
      // siendo la fuente de verdad de lo guardado.
      setTurns((prev) =>
        prev.map((t, i) =>
          i === index ? { ...t, answer: finalAnswer, editing: false, saved: status } : t
        )
      );
      toast.success(
        status === "approved" ? "Guardada en el banco." : "Marcada como rechazada."
      );
      fetchBank();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDocument = async (documentId) => {
    try {
      await openDocumentFile(agentId, documentId);
    } catch (err) {
      toastError(err);
    }
  };

  const handleEndTest = () => {
    setTurns([]);
    setHistory([]);
    setQuestion("");
    handleRemoveImage();
  };

  const handleToggleEdit = (index) => {
    setTurns((prev) =>
      prev.map((t, i) => (i === index ? { ...t, editing: !t.editing } : t))
    );
  };

  const handleEditChange = (index, value) => {
    setTurns((prev) =>
      prev.map((t, i) => (i === index ? { ...t, editValue: value } : t))
    );
  };

  const handleDeleteBankEntry = async (entry) => {
    try {
      await deleteQna(entry.id);
      fetchBank();
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpdateBankStatus = async (entry, status) => {
    try {
      await updateQna(entry.id, { status });
      fetchBank();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Entrenamiento</Title>
        <MainHeaderButtonsWrapper>
          <AgentIaSelector value={agentId} onChange={setAgentId} />
          <Button
            variant="outlined"
            onClick={handleEndTest}
            disabled={turns.length === 0 && history.length === 0}
          >
            Terminar prueba
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        <Typography variant="caption" color="textSecondary">
          Hazle una pregunta como si fueras el cliente. El agente responde
          usando solo la documentación y las respuestas aprobadas de este
          agente, y recuerda lo conversado en esta prueba hasta que le des
          &ldquo;Terminar prueba&rdquo;. Aprueba, corrige o rechaza cada
          respuesta para que vaya aprendiendo — el turno se queda en el
          hilo (marcado como guardado) para que puedas seguir la
          conversación completa de corrido. Puedes adjuntar una imagen
          (ej. captura de la plataforma) — el agente la
          ve para responder, pero al aprobar solo se guarda el texto en el
          banco de respuestas, no la imagen.
        </Typography>

        <div className={classes.chatBox}>
          {turns.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Todavía no hiciste ninguna pregunta.
            </Typography>
          )}
          {turns.map((turn, index) => (
            <div className={classes.turn} key={index}>
              {turn.imageUrl && (
                <img
                  src={turn.imageUrl}
                  alt="Imagen enviada"
                  className={classes.imageThumb}
                  style={{ marginBottom: 4 }}
                />
              )}
              <Typography className={classes.question}>{turn.question}</Typography>

              {turn.editing ? (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={turn.editValue}
                  onChange={(e) => handleEditChange(index, e.target.value)}
                  style={{ marginTop: 6 }}
                />
              ) : (
                <div className={classes.answerBox}>{turn.answer}</div>
              )}

              {turn.saved && (
                <Chip
                  size="small"
                  style={{ marginTop: 6 }}
                  label={turn.saved === "approved" ? "✓ Guardada" : "✗ Rechazada"}
                  color={turn.saved === "approved" ? "primary" : "default"}
                />
              )}

              {turn.equipoLookups?.length > 0 && (
                <Accordion style={{ marginTop: 6 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="caption">
                      🔍 {turn.equipoLookups.length} consulta(s) a InkaMoney
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails style={{ flexDirection: "column", gap: 8 }}>
                    {turn.equipoLookups.map((lookup, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        component="div"
                        style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}
                      >
                        ID consultado: {lookup.id}
                        {"\n"}
                        {JSON.stringify(lookup.result, null, 2)}
                      </Typography>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {turn.sources?.length > 0 && (
                <Accordion style={{ marginTop: 6 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="caption">
                      {turn.sources.length} fuente(s) consultada(s)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails style={{ flexDirection: "column", gap: 8 }}>
                    {turn.sources.map((s, i) => (
                      <div key={i} style={{ marginBottom: 8 }}>
                        <Typography
                          variant="caption"
                          component="div"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {s.type === "document"
                            ? `📄 ${s.document_name || "documento"}: `
                            : "✅ respuesta aprobada previa: "}
                          {s.content}
                        </Typography>
                        {s.type === "document" && s.document_id && (
                          <Button
                            size="small"
                            startIcon={<AttachFileIcon fontSize="small" />}
                            onClick={() => handleOpenDocument(s.document_id)}
                          >
                            Ver archivo
                          </Button>
                        )}
                      </div>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {!turn.saved && (
                <div className={classes.verdictRow}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={() => handleVerdict(index, "approved")}
                  >
                    {turn.editing ? "Guardar corrección" : "Aprobar"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={turn.editing ? <CloseIcon /> : <EditIcon />}
                    onClick={() => handleToggleEdit(index)}
                  >
                    {turn.editing ? "Cancelar edición" : "Corregir"}
                  </Button>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => handleVerdict(index, "rejected")}
                  >
                    Rechazar
                  </Button>
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {pendingImageUrl && (
          <div className={classes.imagePreview}>
            <img src={pendingImageUrl} alt="Para enviar" className={classes.imageThumb} />
            <Typography variant="caption">{pendingImage?.name}</Typography>
            <IconButton size="small" onClick={handleRemoveImage}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}
        <div className={classes.askRow}>
          <input
            ref={imageInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageSelected}
          />
          <IconButton
            disabled={!agentId || asking}
            onClick={() => imageInputRef.current?.click()}
            title="Adjuntar imagen"
          >
            <ImageIcon />
          </IconButton>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Escribe la pregunta tal como la haría un cliente (o solo adjunta una imagen)..."
            value={question}
            disabled={!agentId || asking}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            endIcon={asking ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!agentId || asking || (!question.trim() && !pendingImage)}
            onClick={handleAsk}
          >
            Preguntar
          </Button>
        </div>
      </Paper>

      <Paper variant="outlined" style={{ padding: 16 }}>
        <Typography variant="subtitle2" gutterBottom>
          Banco de respuestas de este agente
        </Typography>
        <div className={classes.bankScroll}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Pregunta</TableCell>
              <TableCell>Respuesta</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bank.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell style={{ maxWidth: 220 }}>{entry.question}</TableCell>
                <TableCell style={{ maxWidth: 320, whiteSpace: "pre-wrap" }}>
                  {entry.answer}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={STATUS_LABEL[entry.status] || entry.status}
                    color={entry.status === "approved" ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <div className={classes.bankRow}>
                    {entry.status !== "approved" && (
                      <IconButton
                        size="small"
                        title="Aprobar"
                        onClick={() => handleUpdateBankStatus(entry, "approved")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      title="Eliminar"
                      onClick={() => handleDeleteBankEntry(entry)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!bankLoading && agentId && bank.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Todavía no hay respuestas guardadas para este agente.
                </TableCell>
              </TableRow>
            )}
            {!agentId && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Elige un agente arriba para ver su banco de respuestas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </div>
        <Divider style={{ marginTop: 8 }} />
        <Typography variant="caption" color="textSecondary">
          Solo las respuestas &ldquo;Aprobadas&rdquo; las usa el agente para
          contestar. Las rechazadas quedan solo de registro.
        </Typography>
      </Paper>
    </MainContainer>
  );
};

export default AgentIaTraining;
